import type { ChangeEvent, FC, MouseEvent } from 'react'
import { useCallback, useMemo, useState } from 'react'
import type { SxProps, Theme } from '@mui/material'
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  IconButton,
  Radio,
  RadioGroup,
  Typography,
  useTheme,
} from '@mui/material'
import { Edit as EditIcon, Send } from '@mui/icons-material'
import type {
  ClaudeSession,
  Command,
  ConversationTurn,
  ConversationTurnEntry,
} from '@/features/events/conversation/state/types'
import { isNotInteractable } from '@/features/events/conversation/state/types'
import type { AskUserQuestionToolInput, Question } from '@/features/events/shared/types'
import { isQuestionnaireActive } from '@/features/events/shared/types'
import type { UUID } from '@/common/types'
import { ToastNotification } from '@/common/components'
import type { AnswersPayload } from '../drafts'
import { useQuestionnaireState, useUserAnswersDrafts } from '../drafts'
import { useCommandSubmitter } from '../controls'
import { useSessionStateContext } from '../state'
import { BaseBubble } from './BaseBubble'
import { UserInputDialog } from '../dialogs/UserInputDialog'
import { CustomUserResponseBox } from '../shared'

interface CollectUserQuestionsBubbleProps {
  readonly event: ConversationTurnEntry
  readonly session: ClaudeSession
  readonly conversationTurn: ConversationTurn | null
  readonly latestEvent: ConversationTurnEntry | null
  readonly allUserCommands: Command[]
  readonly recordCommand: (sessionId: UUID, userPromptId: UUID, userCommand: Command) => void
  readonly onEventDetailsClick?: ((event: ConversationTurnEntry) => void) | undefined
}

interface Answer {
  readonly question: string
  readonly selectedIndices: readonly number[]
  readonly selectedAnswers: readonly string[]
  readonly customAnswer: string | null
}

interface QuestionItemProps {
  readonly question: Question
  readonly questionIndex: number
  readonly totalQuestions: number
  readonly answer: Answer | undefined
  readonly error: string | undefined
  readonly onRadioChange: (
    questionHeader: string,
    questionText: string,
    optionIndex: number,
    optionLabel: string,
  ) => void
  readonly onCheckboxChange: (
    questionHeader: string,
    questionText: string,
    optionIndex: number,
    optionLabel: string,
  ) => void
  readonly onOpenCustomInputDialog: (question: Question, questionIndex: number) => void
  readonly isActive: boolean
  readonly theme: Theme
}

export const CollectUserQuestionsBubble: FC<CollectUserQuestionsBubbleProps> = ({
  event,
  session,
  conversationTurn,
  latestEvent,
  allUserCommands,
  recordCommand,
  onEventDetailsClick,
}) => {
  const theme = useTheme()
  const [showDraftSaved, setShowDraftSaved] = useState(false)
  const { updateUserAnswersDraft } = useSessionStateContext()
  const toolInput = event.notificationContext?.toolInput as AskUserQuestionToolInput | undefined
  const questions = toolInput?.questions ?? []

  const isActive = isQuestionnaireActive(event, latestEvent, allUserCommands)

  const handleDraftSaved = useCallback(() => {
    setShowDraftSaved(true)
  }, [])

  const { saveBulkAnswers, clearAllAnswers } = useUserAnswersDrafts({
    userAnswersDraft: session.runtime.memory.userAnswersDraft,
    onUserAnswersDraftChange: useCallback(
      (draft) => updateUserAnswersDraft(session.sessionId, draft),
      [session.sessionId, updateUserAnswersDraft],
    ),
    eventId: event.eventId,
  })

  const submitter = useCommandSubmitter({
    session,
    conversationTurn,
    recordCommand,
  })

  const handleSubmit = useCallback(
    async (payload: AnswersPayload): Promise<void> => {
      await submitter.submitQuestionnaireAnswers(event, payload)
    },
    [submitter, event],
  )

  const questionnaireState = useQuestionnaireState({
    eventId: event.eventId,
    questions,
    userAnswersDraft: session.runtime.memory.userAnswersDraft,
    onSaveDraft: saveBulkAnswers,
    onSubmit: handleSubmit,
    onSubmitSuccess: clearAllAnswers,
    onError: useCallback(() => {
      /* ignored */
    }, []),
  })

  const hasSubmitted = useMemo(
    () =>
      allUserCommands.some((cmd) => cmd.refEventId === event.eventId && cmd.userAnswers !== null),
    [allUserCommands, event.eventId],
  )

  const submittedAnswersPayload = useMemo(() => {
    const cmd = allUserCommands.find(
      (cmd) => cmd.refEventId === event.eventId && cmd.userAnswers !== null,
    )
    return cmd?.userAnswers as AnswersPayload | undefined
  }, [allUserCommands, event.eventId])

  if (questions.length === 0) {
    return null
  }

  if (isNotInteractable(session)) {
    return (
      <BaseBubble event={event} showTimestamp={true} onEventDetailsClick={onEventDetailsClick}>
        <Box sx={contentContainerSx}>
          <Box sx={headerContainerSx}>
            <Typography variant="h6" sx={mainHeaderSx}>
              Agent Questions
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={instructionsSx}>
              The agent is asking questions. Check your Claude Code session to respond.
            </Typography>
          </Box>

          {questions.map((question, questionIndex) => (
            <Box key={question.header} sx={questionContainerSx}>
              <Box sx={fieldContainerSx}>
                <Typography variant="caption" sx={questionNumberSx}>
                  Question {questionIndex + 1}/{questions.length}
                </Typography>
                <Typography variant="subtitle1" sx={questionTextSx}>
                  {question.question}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={questionHeaderLabelSx}>
                  {question.header}
                </Typography>
              </Box>

              {question.options.map((option, optionIndex) => (
                <Box
                  key={`${question.header}-${option.label}-${optionIndex}`}
                  sx={readOnlyOptionItemSx}
                >
                  <Typography variant="body1" sx={optionLabelSx}>
                    {option.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={optionDescriptionSx}>
                    {option.description}
                  </Typography>
                </Box>
              ))}
            </Box>
          ))}
        </Box>
      </BaseBubble>
    )
  }

  if (hasSubmitted && submittedAnswersPayload) {
    return (
      <BaseBubble
        event={event}
        showTimestamp={true}
        borderColor={theme.palette.text.disabled}
        onEventDetailsClick={onEventDetailsClick}
      >
        <Box sx={contentContainerSx}>
          {questions.map((question) => {
            const answer = submittedAnswersPayload.answers[question.header]
            if (!answer) return null

            return (
              <Box key={question.header} sx={summaryQuestionContainerSx}>
                <Typography variant="body2" sx={summaryQuestionTextSx}>
                  {question.question}
                </Typography>
                {answer.selectedAnswers.length > 0 && (
                  <Typography variant="body2" sx={summaryAnswerTextSx(theme)}>
                    → {answer.selectedAnswers.join(', ')}
                  </Typography>
                )}
                {answer.customAnswer && (
                  <CustomUserResponseBox
                    content={answer.customAnswer}
                    label="Custom answer"
                    color={theme.palette.warning.main}
                  />
                )}
              </Box>
            )
          })}
        </Box>
      </BaseBubble>
    )
  }

  return (
    <BaseBubble event={event} showTimestamp={true} onEventDetailsClick={onEventDetailsClick}>
      <Box sx={contentContainerSx}>
        <Box sx={headerContainerSx}>
          <Typography variant="h6" sx={mainHeaderSx}>
            Answer Questions
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={instructionsSx}>
            Please answer the following questions to help Claude Code proceed with your request.
          </Typography>
        </Box>

        {questions.map((question, questionIndex) => (
          <QuestionItem
            key={question.header}
            question={question}
            questionIndex={questionIndex}
            totalQuestions={questions.length}
            answer={questionnaireState.answers.get(question.header)}
            error={questionnaireState.validationErrors.get(question.header)}
            onRadioChange={questionnaireState.handleRadioChange}
            onCheckboxChange={questionnaireState.handleCheckboxChange}
            onOpenCustomInputDialog={questionnaireState.handleOpenCustomInputDialog}
            isActive={isActive}
            theme={theme}
          />
        ))}

        <Box sx={submitContainerSx}>
          <Button
            variant="contained"
            size="large"
            startIcon={<Send />}
            onClick={questionnaireState.handleSubmitAnswers}
            disabled={!isActive || questionnaireState.isSubmitting || hasSubmitted}
            sx={submitButtonSx(theme)}
          >
            {questionnaireState.isSubmitting ? 'Submitting...' : 'Submit Answers'}
          </Button>
        </Box>
      </Box>

      <ToastNotification
        open={questionnaireState.showError}
        message={questionnaireState.errorMessage}
        severity="error"
        onClose={() => questionnaireState.setShowError(false)}
      />

      {questionnaireState.customInputDialogQuestion &&
        questionnaireState.customInputDialogQuestionIndex !== null &&
        session && (
          <UserInputDialog
            key={`questionnaire-${questionnaireState.customInputDialogQuestionIndex}`}
            open={true}
            onClose={questionnaireState.handleCloseCustomInputDialog}
            onSubmit={questionnaireState.handleSubmitCustomInput}
            session={session}
            isSubmitting={false}
            error={null}
            draftType="custom_answer"
            refEventId={null}
            title="Custom Answer"
            placeholder="Type your custom answer..."
            description={`${questionnaireState.customInputDialogQuestion.question}

Provide your custom answer to this question.
Your draft is saved when you close this dialog and restored when you reopen it.
Drafts are cleared only after successful submission.`}
            currentAgentMode={event.agentMode}
            agentModeSelectable={false}
            submitButtonLabel="Save"
            submitButtonLoadingLabel="Saving..."
            onDraftSaved={handleDraftSaved}
            initialValue={
              questionnaireState.answers.get(questionnaireState.customInputDialogQuestion.header)
                ?.customAnswer ?? undefined
            }
            deleteOnSubmit={false}
            questionHeader={questionnaireState.customInputDialogQuestion.header}
            eventId={event.eventId}
          />
        )}

      <ToastNotification
        open={showDraftSaved}
        message="Draft saved"
        severity="info"
        onClose={() => setShowDraftSaved(false)}
        autoHideDuration={3000}
      />
    </BaseBubble>
  )
}

const QuestionItem: FC<QuestionItemProps> = ({
  question,
  questionIndex,
  totalQuestions,
  answer,
  error,
  onRadioChange,
  onCheckboxChange,
  onOpenCustomInputDialog,
  isActive,
  theme,
}) => {
  const handleOtherOptionClick = useCallback(
    () => onOpenCustomInputDialog(question, questionIndex),
    [question, questionIndex, onOpenCustomInputDialog],
  )

  const handleEditIconClick = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation()
      onOpenCustomInputDialog(question, questionIndex)
    },
    [question, questionIndex, onOpenCustomInputDialog],
  )

  const handleRadioGroupChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value
      if (value === 'custom') {
        onOpenCustomInputDialog(question, questionIndex)
      } else {
        const optionIndex = Number.parseInt(value, 10)
        const option = question.options[optionIndex]
        if (option) {
          onRadioChange(question.header, question.question, optionIndex, option.label)
        }
      }
    },
    [question, questionIndex, onOpenCustomInputDialog, onRadioChange],
  )

  const createCheckboxChangeHandler = useCallback(
    (optionIndex: number, optionLabel: string) => () => {
      onCheckboxChange(question.header, question.question, optionIndex, optionLabel)
    },
    [question.header, question.question, onCheckboxChange],
  )

  return (
    <Box sx={questionContainerSx}>
      <Box sx={fieldContainerSx}>
        <Typography variant="caption" sx={questionNumberSx}>
          Question {questionIndex + 1}/{totalQuestions}
        </Typography>
        <Typography variant="subtitle1" sx={questionTextSx}>
          {question.question}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={questionHeaderLabelSx}>
          {question.header}
        </Typography>
      </Box>

      {question.multiSelect ? (
        <FormGroup>
          {question.options.map((option, optionIndex) => (
            <FormControlLabel
              key={`${question.header}-${option.label}-${optionIndex}`}
              control={
                <Checkbox
                  checked={answer?.selectedIndices.includes(optionIndex) ?? false}
                  onChange={createCheckboxChangeHandler(optionIndex, option.label)}
                  disabled={!isActive}
                  sx={checkboxSx(theme)}
                />
              }
              label={
                <Box sx={optionLabelContainerSx}>
                  <Typography variant="body1" sx={optionLabelSx}>
                    {option.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={optionDescriptionSx}>
                    {option.description}
                  </Typography>
                </Box>
              }
              sx={formControlLabelSx}
            />
          ))}
          <FormControlLabel
            key={`${question.header}-other`}
            control={
              <Checkbox
                checked={!!answer?.customAnswer}
                onChange={handleOtherOptionClick}
                disabled={!isActive}
                sx={checkboxSx(theme)}
              />
            }
            label={
              <Box>
                <Box sx={otherOptionLabelContainerSx}>
                  <Typography variant="body1" sx={optionLabelSx}>
                    Other
                  </Typography>
                  {answer?.customAnswer && (
                    <IconButton
                      size="small"
                      onClick={handleEditIconClick}
                      disabled={!isActive}
                      sx={inlineEditButtonSx(theme)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
                {answer?.customAnswer && (
                  <CustomUserResponseBox
                    content={answer.customAnswer}
                    color={theme.palette.warning.main}
                  />
                )}
              </Box>
            }
            sx={formControlLabelSx}
          />
        </FormGroup>
      ) : (
        <RadioGroup
          value={answer?.customAnswer ? 'custom' : (answer?.selectedIndices[0] ?? '')}
          onChange={handleRadioGroupChange}
        >
          {question.options.map((option, optionIndex) => (
            <FormControlLabel
              key={`${question.header}-${option.label}-${optionIndex}`}
              value={optionIndex}
              control={<Radio disabled={!isActive} sx={radioSx(theme)} />}
              label={
                <Box sx={optionLabelContainerSx}>
                  <Typography variant="body1" sx={optionLabelSx}>
                    {option.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={optionDescriptionSx}>
                    {option.description}
                  </Typography>
                </Box>
              }
              sx={formControlLabelSx}
            />
          ))}
          <FormControlLabel
            key={`${question.header}-other`}
            value="custom"
            control={<Radio disabled={!isActive} sx={radioSx(theme)} />}
            label={
              <Box>
                <Box sx={otherOptionLabelContainerSx}>
                  <Typography variant="body1" sx={optionLabelSx}>
                    Other
                  </Typography>
                  {answer?.customAnswer && (
                    <IconButton
                      size="small"
                      onClick={handleEditIconClick}
                      disabled={!isActive}
                      sx={inlineEditButtonSx(theme)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
                {answer?.customAnswer && (
                  <CustomUserResponseBox
                    content={answer.customAnswer}
                    color={theme.palette.warning.main}
                  />
                )}
              </Box>
            }
            sx={formControlLabelSx}
          />
        </RadioGroup>
      )}

      {error && (
        <Typography variant="caption" sx={errorTextSx(theme)}>
          {error}
        </Typography>
      )}
    </Box>
  )
}

const contentContainerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  gap: 1.5,
}

const headerContainerSx: SxProps<Theme> = {
  mb: 3,
}

const mainHeaderSx: SxProps<Theme> = {
  fontWeight: 500,
  mb: 1,
}

const instructionsSx: SxProps<Theme> = {
  lineHeight: 1.6,
}

const summaryQuestionContainerSx: SxProps<Theme> = {
  mb: 2,
  pb: 2,
  borderBottom: '1px solid',
  borderColor: 'divider',
  '&:last-child': {
    borderBottom: 'none',
    mb: 0,
    pb: 0,
  },
}

const summaryQuestionTextSx: SxProps<Theme> = {
  fontWeight: 500,
  mb: 0.5,
  fontSize: 'inherit',
}

const summaryAnswerTextSx = (theme: Theme): SxProps<Theme> => ({
  color: theme.palette.warning.main,
  ml: 1,
  fontSize: 'inherit',
})

const submitContainerSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'center',
  mt: 2,
  mb: 3,
}

const submitButtonSx = (theme: Theme): SxProps<Theme> => ({
  paddingX: 3,
  paddingY: 1.25,
  fontWeight: 500,
  backgroundColor: theme.palette.primary.main,
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
  },
  fontSize: 'inherit',
})

const questionContainerSx: SxProps<Theme> = {
  mb: 3,
  p: 3,
  borderRadius: 2,
  backgroundColor: 'transparent',
  border: '1px solid',
  borderColor: 'divider',
}

const fieldContainerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  gap: 0.5,
}

const questionNumberSx: SxProps<Theme> = {
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  color: 'text.secondary',
  fontSize: 'inherit',
}

const questionTextSx: SxProps<Theme> = {
  fontWeight: 500,
  mt: 0.5,
  mb: 1,
  fontSize: 'inherit',
}

const questionHeaderLabelSx: SxProps<Theme> = {
  fontStyle: 'italic',
  color: 'text.secondary',
  fontSize: 'inherit',
}

const formControlLabelSx: SxProps<Theme> = {
  alignItems: 'flex-start',
  mb: 1.5,
}

const optionLabelContainerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  gap: 0.25,
}

const readOnlyOptionItemSx: SxProps<Theme> = {
  ml: 2,
  mb: 1,
}

const optionLabelSx: SxProps<Theme> = {
  fontWeight: 400,
}

const optionDescriptionSx: SxProps<Theme> = {
  lineHeight: 1.5,
  color: 'text.secondary',
  fontSize: 'inherit',
}

const radioSx = (theme: Theme): SxProps<Theme> => ({
  color: theme.palette.text.secondary,
  '&.Mui-checked': {
    color: theme.palette.primary.main,
  },
})

const checkboxSx = (theme: Theme): SxProps<Theme> => ({
  color: theme.palette.text.secondary,
  '&.Mui-checked': {
    color: theme.palette.success.main,
  },
})

const otherOptionLabelContainerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: 0.5,
}

const inlineEditButtonSx = (theme: Theme): SxProps<Theme> => ({
  color: theme.palette.text.secondary,
  padding: '2px',
  marginLeft: '4px',
  '&:hover': {
    backgroundColor: 'transparent',
    color: theme.palette.primary.main,
  },
})

const errorTextSx = (theme: Theme): SxProps<Theme> => ({
  color: theme.palette.error.main,
  mt: 1,
  display: 'block',
  fontSize: 'inherit',
})
